import React, { useState } from 'react';
import GalleryGrid from '../components/GalleryGrid';
import ImageModal from '../components/ImageModal';
import Footer from '../components/Footer';
import { galleryItems, videoItems } from '../sources/galleryItems';
import '../styles/gallery.css';

const GalleryPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const items = galleryItems;

  const handleOpenModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const handleVideoModal = (video) => {
    setSelectedImage(video);
    setIsModalOpen(true);
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="gallery-header" data-aos="fade-up">
        <h1>AI Image Gallery</h1>
        <p className="text-muted">
          Where carefully engineered prompts meet high-quality AI image
          generation.
        </p>
      </div>

      {/* Gallery Grid */}
      <GalleryGrid items={items} onOpenModal={handleOpenModal} />

      {/* AI Videos Section */}
      <div className="video-section" data-aos="fade-up">
        <div className="section-header">
          <h2>AI Generated Videos</h2>
          <p className="text-muted">
            Exploring the frontier of AI video generation with dynamic motion
            and cinematic quality.
          </p>
        </div>

        <div className="video-grid">
          {videoItems.map((video, index) => (
            <div
              key={index}
              className="video-item"
              data-ratio={video.ratio}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              onClick={() => handleVideoModal(video)}
            >
              <video
                src={video.src}
                autoPlay
                loop
                muted
                playsInline
                className="video-preview"
              />
              <div className="video-overlay">
                <div className="video-info">
                  <div className="info-label">Click to view details</div>
                  <div className="info-prompt">{video.tools}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Tools Section */}
      <div className="ai-tools-section" data-aos="fade-up">
        <div className="tools-header">
          <h2>AI Tools & Platforms Used</h2>
          <p className="text-muted">
            Explore the cutting-edge AI platforms powering this creative work
          </p>
        </div>

        <div className="ai-tools-grid">
          <a
            href="https://aistudio.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="tool-card"
            data-tooltip="Google Veo 3.1"
            data-aos="fade-up"
            data-aos-delay="0"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          </a>

          <a
            href="https://openart.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="tool-card openart-logo"
            data-tooltip="OpenArt AI"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <img
              src="https://cdn.openart.ai/assets/home/openart_logo_20240529.svg"
              alt="OpenArt AI"
            />
          </a>

          <a
            href="https://klingai.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="tool-card"
            data-tooltip="Kling AI"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <img
              src="https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/kling-ai-icon.png"
              alt="Kling AI"
            />
          </a>

          <a
            href="https://ltx.video/"
            target="_blank"
            rel="noopener noreferrer"
            className="tool-card"
            data-tooltip="LTX Video"
            data-aos="fade-up"
            data-aos-delay="300"
          >
            <img
              src="https://cdn.prod.website-files.com/68872d15af29880764eac4aa/695b921e1b7acc281d560ff4_LTX%20Logo%20for%20structured%20data.png"
              alt="LTX Video"
            />
          </a>

          <a
            href="https://blackforestlabs.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="tool-card flux-logo"
            data-tooltip="Flux"
            data-aos="fade-up"
            data-aos-delay="400"
          >
            <img
              src="https://bfl.ai/brand/symbol-white.png"
              alt="Flux"
            />
          </a>

          <a
            href="https://huggingface.co/"
            target="_blank"
            rel="noopener noreferrer"
            className="tool-card"
            data-tooltip="Hugging Face"
            data-aos="fade-up"
            data-aos-delay="500"
          >
            <img
              src="https://huggingface.co/datasets/huggingface/brand-assets/resolve/main/hf-logo.png"
              alt="Hugging Face"
            />
          </a>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Image Modal */}
      <ImageModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        image={selectedImage}
      />
    </div>
  );
};

export default GalleryPage;